import { IconButton, ListItemIcon, Menu, MenuItem, MenuList, Tooltip, Typography } from "@mui/material";
import { MouseEvent, useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Link } from "react-router-dom";

export interface CrmMenuItemProps {
  title: string;
  tooltip: string;
  dataTestId: string;
  onClick?: (ev: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
  isLink?: boolean;
  href?: string;
  icon?: JSX.Element;
  disabled?: boolean;
  visible?: boolean;
}

function CRMMenuItem({ props }: { props: CrmMenuItemProps }) {
  return (
    <Tooltip title={props.tooltip} disableInteractive arrow>
      {/* span allows the tooltip to work on disabled menu items */}
      <span>
        {props.isLink ? (
          <MenuItem
            hidden={!props.visible}
            component={Link}
            data-test={props.dataTestId}
            aria-label={props.title}
            disabled={props.disabled ?? false}
            to={props.href ?? ""}
          >
            {props.icon && <ListItemIcon>{props.icon}</ListItemIcon>}
            <Typography variant="body2" color="text.secondary">
              {props.title}
            </Typography>
          </MenuItem>
        ) : (
          <MenuItem
            hidden={!props.visible}
            onClick={(event: any) => {
              if (!(props.disabled ?? false) && props.onClick) {
                props.onClick?.(event);
              }
              event.stopPropagation();
              event.preventDefault();
            }}
            component={"li"}
            data-test={props.dataTestId}
            aria-label={props.title}
            disabled={props.disabled ?? false}
          >
            {props.icon && <ListItemIcon>{props.icon}</ListItemIcon>}
            <Typography variant="body2" color="text.secondary">
              {props.title}
            </Typography>
          </MenuItem>
        )}
      </span>
    </Tooltip>
  );
}

export default function CRMMenu({
  id,
  title,
  links,
  size = "medium",
}: {
  id: string;
  title: string;
  links: CrmMenuItemProps[];
  size?: "small" | "medium" | "large";
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
    event.preventDefault();
  };
  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(null);
    event.stopPropagation();
    event.preventDefault();
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        aria-controls={open ? id : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-label={title}
        id={`${id}-button`}
        title={title}
        data-test={id}
        size={size}
      >
        <MoreVertIcon fontSize={size} />
      </IconButton>
      <Menu
        id={id}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": `${id}-button`,
        }}
        onClick={(ev) => {
          ev.stopPropagation();
          ev.preventDefault();
        }}
        data-test={`${id}-menu`}
      >
        <MenuList dense>
          {links.map((link, idx) => (
            <CRMMenuItem
              key={`menu-item-${link.title}-${idx}`}
              props={{
                ...link,
                onClick: (ev) => {
                  link.onClick?.(ev);
                  setAnchorEl(null);
                },
              }}
            />
          ))}
        </MenuList>
      </Menu>
    </>
  );
}
